import { useEffect, useState, type ReactNode } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { supabase } from '../../lib/supabaseClient';
import {
  anularCanje, borrarCanje, corregirCanje, jornadasParaCanjes, listCanjesBandeja, listCorreccionesDeCanje,
  subirFotoDeCorreccion,
} from '../../api/canjes';
import type { CanjeRow, CorreccionRow, JornadaCanje } from '../../api/canjes';
import { agruparCanjesPorTurno } from '../../domain/canjesPorTurno';
import { campaniasQueCoordino } from '../../api/campaigns';
import {
  descargarFotosEmbebidas, firmarFotosParaReporte, getPhotoUrl,
} from '../../api/attendance';
import { compressImage } from '../../lib/compressImage';
import { rechazoDeArchivo } from '../../lib/archivoDeFoto';
import { useAuth } from '../../components/AuthProvider';
// El campo con casilla «cambiar» lo comparten esta bandeja y la del levantamiento: la promesa que
// hace en pantalla («los que no marques quedan deshabilitados mostrando lo que dice hoy el
// registro») la sostienen detalles que no pueden estar escritos dos veces.
import { CampoCorregible } from '../../components/CampoCorregible';
import { puedeEditar, soloSupervisaTiendas } from '../../domain/permissions';
// La pastilla de señal es una sola en toda la app: la misma que usan la bandeja de visitas y el
// tablero de Hoy. Tres copias de «tardío» en tres tonos de ámbar distintos era lo que había.
import { Senal } from '../../components/DetalleExpandible';
import { buildCanjesWorkbook, reporteFileName } from '../../domain/excel/exportReportes';
import { descargarHtml, descargarXlsx, imprimirHtml } from '../../lib/descargas';
import {
  buildCanjesHtml, reporteCanjesHtmlFileName, rutasDeFotoDeTodos,
} from '../../domain/html/reporteCanjes';
import BorrarRegistro from '../../components/BorrarRegistro';
import TarjetasResumen from '../../components/TarjetasResumen';
import CargaManualCanje, { type TurnoFijo } from './CargaManualCanje';
import CanjesPorTurno from './CanjesPorTurno';

const TZ = 'America/Lima';
/** Perú no tiene horario de verano: el offset es -05:00 todo el año. */
const LIMA_OFFSET = '-05:00';

interface CampaignOption { id: string; client: string; brand: string; description: string | null }
interface PdvOption { id: string; code: string; name: string }
interface DinamicaOption { id: string; code: string; name: string }
interface WorkerOption { id: string; nombre: string }

/**
 * Etiquetas de las banderas. El servidor las calcula; acá solo se nombran y se pintan.
 *
 * Ninguna es un error: son la calidad del dato puesta a la vista. Un registro tardío fuera de
 * programación puede ser perfectamente legítimo —trabajó otro día, o no la dejaron usar el
 * celular en sala— y por eso el color es ámbar y no rojo.
 */
const BANDERAS: { key: keyof CanjeRow; label: string; titulo: string }[] = [
  {
    key: 'fueraDeProgramacion', label: 'Fuera de programación',
    titulo: 'Ese día no tenía esa tienda asignada. Se pidió la tienda de la lista de la campaña.',
  },
  {
    key: 'registroTardio', label: 'Registro tardío',
    titulo: 'Se registró después de la fecha que declara.',
  },
  {
    key: 'sinAsistencia', label: 'Sin asistencia',
    titulo: 'No marcó ingreso ese día. El canje entra igual.',
  },
];

/** Nombres legibles de los campos corregibles, para el historial. */
const CAMPO_LABEL: Record<string, string> = {
  fecha: 'Fecha',
  ocurrido_en: 'Momento declarado',
  cliente_nombre: 'Cliente',
  cliente_contacto: 'Contacto',
  monto_compra: 'Monto',
  observaciones: 'Observaciones',
  pdv_id: 'Tienda',
  boleta_numero: 'N.º de boleta',
  // Sin estas dos el historial mostraría `foto_boleta_path` en crudo, que es el nombre de la
  // columna y no algo que el jefe de operaciones tenga por qué reconocer.
  foto_boleta_path: 'Foto de boleta',
  foto_premio_path: 'Foto de premio',
  estado: 'Estado',
};

/**
 * ¿Se le ofrecen corregir y anular ESTE registro? Espejo de `puede_editar_canje` (0044).
 *
 * El jefe de operaciones puede con todo; el coordinador, solo con las campañas donde es el
 * designado. **El rol no alcanza**: `coordinador` lo comparten el coordinador y los supervisores de
 * la campaña, así que decidir por rol le mostraría a un supervisor botones que el servidor rechaza.
 *
 * `esValidador` (rol `datos`, 0061) entra por la puerta CONTRARIA, y por eso va nombrado aparte:
 * el servidor YA lo autoriza —`puede_editar_canje` empieza con `has_role('admin') or
 * has_role('datos')`, verificado contra la base el 10-set— pero esta función solo miraba admin y
 * campañas coordinadas, y el validador no coordina ninguna (ve todas por su rol, que es el punto
 * de la 0061). El conjunto le quedaba vacío y **los botones no se dibujaban nunca**.
 *
 * Es exactamente el mismo defecto que el usuario reportó el 2026-08-22 en la bandeja de
 * levantamiento como «no le permitía editar», y que allá se arregló con `esValidador` en
 * `puedeAccionarFilaVisita`. Acá quedó sin arreglar hasta el 10-set: dos bandejas hermanas, el
 * mismo permiso en el servidor, y solo una lo respetaba. Se ve peor que un botón que rebota,
 * porque no hay error que leer: simplemente no hay botón.
 *
 * Función pura y exportada para poder probarla: es una regla de permiso, y una regla de permiso que
 * solo existe dentro de un componente no se puede verificar.
 */
export function puedeAccionarFila(
  esAdmin: boolean, esValidador: boolean,
  campaniasCoordinadas: ReadonlySet<string>, campaignId: string,
): boolean {
  return esAdmin || esValidador || campaniasCoordinadas.has(campaignId);
}

/**
 * De lo que está a la vista, a qué filas se les puede cambiar la fecha.
 *
 * Se excluyen las anuladas: `corregir_canje` las acepta —solo `anular_canje` mira el estado—,
 * así que sin este filtro la pantalla dejaría refechar un registro que ya no cuenta para nada
 * y el rastro se llenaría de correcciones sobre canjes muertos.
 */
export function filasRefechables(
  esAdmin: boolean, esValidador: boolean,
  campaniasCoordinadas: ReadonlySet<string>, filas: CanjeRow[],
): CanjeRow[] {
  return filas.filter(
    (f) => f.estado === 'registrado'
      && puedeAccionarFila(esAdmin, esValidador, campaniasCoordinadas, f.campaignId),
  );
}

/**
 * Lo que el modal de corrección tiene en pantalla.
 *
 * **Ojo con la lectura vieja de esta estructura: vacío ya NO significa "no se toca ese campo".**
 * Qué se toca lo dice ahora el conjunto de campos MARCADOS, aparte; acá solo viven los valores. El
 * cambio es lo que pidió el usuario (2026-08-19): *"la corrección me obligan a llenar todos los
 * campos, quiero versatilidad para solo cambiar un solo campo"*. Técnicamente nunca obligó —el
 * formulario venía precargado y solo viajaba el diff—, pero ocho campos llenos se leen como ocho
 * campos por revisar, y encima "vacío = no se toca" hacía imposible BORRAR un dato: dejar el campo
 * en blanco era indistinguible de no haberlo tocado.
 */
export interface FormCorreccion {
  fecha: string;
  horaDeclarada: string;
  clienteNombre: string;
  clienteContacto: string;
  montoCompra: string;
  observaciones: string;
  pdvId: string;
  boletaNumero: string;
}

/** Los campos de texto/fecha que se pueden marcar. Las fotos van aparte: su valor es un archivo. */
export type CampoCorreccion = keyof FormCorreccion;

/**
 * Cómo se llama cada campo EN PANTALLA.
 *
 * Un solo mapa para la etiqueta del control y para los avisos que hablan de él: un aviso que diga
 * «Fecha está marcada y vacía» mientras el control se rotula de otra forma manda a buscar un campo
 * que no existe con ese nombre.
 */
export const ETIQUETA_CAMPO: Record<CampoCorreccion, string> = {
  fecha: 'Fecha',
  horaDeclarada: 'Hora declarada',
  clienteNombre: 'Cliente',
  clienteContacto: 'Contacto',
  montoCompra: 'Monto (S/)',
  observaciones: 'Observaciones',
  pdvId: 'Tienda',
  boletaNumero: 'N.º de boleta',
};

/**
 * Rutas de las fotos YA SUBIDAS para esta corrección.
 *
 * Llegan como parámetro y no salen de `FormCorreccion` porque subirlas es asíncrono y falible: si
 * la subida se hiciera dentro del cálculo del diff, una función pura tendría que esperar a la red
 * y dejaría de poder probarse sin montar nada. Acá entra el resultado, ya resuelto.
 */
export interface FotosDeCorreccion {
  boleta?: string;
  premio?: string;
}

/**
 * Campos que la base declara NOT NULL: se pueden CAMBIAR pero no vaciar.
 *
 * `fecha` y `pdv_id` nacen `not null` en la 0024 y nadie los aflojó después (`cliente_nombre` sí,
 * en la 0030, y `foto_boleta_path` en la 0036). Mandarlos vacíos haría que el servidor rechace la
 * corrección entera —incluidos los otros campos de la misma tanda—, así que se filtran acá y la
 * pantalla lo dice al lado del control.
 */
const NO_VACIABLES: readonly CampoCorreccion[] = ['fecha', 'pdvId'];

/**
 * ¿Marcar este campo y dejarlo en blanco borra el dato, o el servidor lo va a rechazar?
 *
 * Se exporta para que la pantalla y el diff lean la MISMA lista. Si un día se afloja un NOT NULL
 * —ya pasó dos veces, `cliente_nombre` en la 0030 y `foto_boleta_path` en la 0036— se corrige en
 * un solo sitio, en vez de que el aviso de la pantalla siga diciendo "obligatorio" para siempre.
 */
export function sePuedeVaciar(campo: CampoCorreccion): boolean {
  return !NO_VACIABLES.includes(campo);
}

/**
 * Por qué NO se puede guardar todavía, o `null` si sí se puede.
 *
 * Devuelve el texto en vez de un booleano para poder ponerlo a la vista junto al botón apagado.
 * Antes el botón se podía pulsar sin nada marcado y recién ahí aparecía "No cambiaste ningún
 * campo": enterarse después de pulsar es enterarse tarde, y en una pantalla de corrección deja la
 * duda de si el guardado entró a medias.
 *
 * `form` es opcional solo para que llamarla sin modal abierto no obligue a inventar uno; con el
 * modal abierto SIEMPRE se pasa.
 */
export function bloqueoDeCorreccion(
  marcados: ReadonlySet<CampoCorreccion>, hayFotoNueva: boolean, motivo: string,
  form?: FormCorreccion,
): string | null {
  if (marcados.size === 0 && !hayFotoNueva) {
    return 'Marca al menos un campo para cambiar, o elige una foto nueva.';
  }
  // Un campo OBLIGATORIO marcado y en blanco no es una operación posible, y callarlo era peor que
  // rechazarlo: `cambiosDe` lo descarta y el guardado seguía adelante con los demás campos
  // diciendo «Corrección guardada» — o sea, se anunciaba como hecho justo lo que se ignoró. El
  // caso concreto: marcar «Fecha», borrar el input, marcar además otro campo y guardar.
  if (form !== undefined) {
    const vacio = NO_VACIABLES.find((c) => marcados.has(c) && form[c].trim() === '');
    if (vacio !== undefined) {
      return `Marcaste «${ETIQUETA_CAMPO[vacio]}» y la dejaste vacía, y es obligatoria: elige un `
        + 'valor o desmarca la casilla.';
    }
  }
  if (motivo.trim() === '') return 'Escribe el motivo de la corrección.';
  return null;
}

/**
 * ¿Este campo del historial es una RUTA de foto? Decide si el valor se pinta como enlace o como
 * texto. Las dos columnas se nombran acá y no en la pantalla para que no se desincronicen del
 * `CAMPO_LABEL` de arriba.
 */
export function esCampoDeFoto(campo: string): boolean {
  return campo === 'foto_boleta_path' || campo === 'foto_premio_path';
}

export function formDesde(r: CanjeRow): FormCorreccion {
  return {
    fecha: r.fecha,
    horaDeclarada: r.ocurridoEn ? formatInTimeZone(new Date(r.ocurridoEn), TZ, 'HH:mm') : '',
    clienteNombre: r.clienteNombre ?? '',
    clienteContacto: r.clienteContacto ?? '',
    montoCompra: r.montoCompra === null ? '' : String(r.montoCompra),
    observaciones: r.observaciones ?? '',
    pdvId: r.pdv?.id ?? '',
    boletaNumero: r.boletaNumero ?? '',
  };
}

/**
 * El formulario con UN campo repuesto desde el registro. Es lo que pasa al DESMARCAR su casilla.
 *
 * Se extrae del componente por la misma razón que `puedeAccionarFila`: una regla que solo vive
 * dentro de un componente no se puede verificar. Y esta sostiene una promesa escrita en pantalla
 * —«los que no marques quedan deshabilitados mostrando lo que dice hoy el registro»—: sin reponer,
 * el control apagado se quedaba mostrando lo tecleado, que no es ni lo que dice el registro ni lo
 * que se va a guardar.
 */
export function reponerCampo(
  r: CanjeRow, f: FormCorreccion, campo: CampoCorreccion,
): FormCorreccion {
  return { ...f, [campo]: formDesde(r)[campo] };
}

/**
 * Solo los campos que de verdad cambiaron, en la forma que espera la RPC (texto plano; el
 * servidor castea al tipo de la columna y rechaza cualquier campo fuera de su lista blanca).
 *
 * Mandar el registro completo haría que el rastro guardara "correcciones" donde nada cambió, y
 * el historial dejaría de servir para lo único que sirve: saber qué se tocó y por qué.
 */
/**
 * Los cambios que produce mover un registro a OTRA FECHA, y nada más.
 *
 * Es lo único que hace falta para el caso real que lo motivó: una impulsadora que no pudo
 * cargar en el momento y subió el sábado y el domingo con fecha del lunes. Ahí no hay nada
 * que corregir salvo el día.
 *
 * `ocurrido_en` viaja con la fecha. Lleva día Y hora, así que moviendo solo `fecha` el
 * registro queda diciendo dos cosas distintas: la fecha nueva en una columna y el momento
 * declarado en el día viejo en la otra. La hora del día se conserva tal cual: lo que estaba
 * mal era el día, no la hora a la que ocurrió.
 *
 * Se extrae a su propia función porque la usan el modal de corrección completo y el de
 * "cambiar fecha": dos copias de esta regla se desincronizan, y la que se olvida de
 * actualizar deja registros con la hora en otro día sin avisar a nadie.
 */
export function cambiosDeFecha(r: CanjeRow, fecha: string): Record<string, string> {
  if (fecha === '' || fecha === r.fecha) return {};
  const out: Record<string, string> = { fecha };
  if (r.ocurridoEn !== null) {
    const hora = formatInTimeZone(new Date(r.ocurridoEn), TZ, 'HH:mm');
    out.ocurrido_en = `${fecha}T${hora}:00${LIMA_OFFSET}`;
  }
  return out;
}

/**
 * El diff que viaja a `corregir_canje`: solo los campos MARCADOS y que además cambiaron.
 *
 * Las dos condiciones hacen falta y ninguna sobra:
 *
 * - **Marcado** es la versatilidad que pidió el usuario. Sin él, un campo que la pantalla trae
 *   precargado y alguien roza sin querer viaja igual; con él, tocar la fecha es marcar «Fecha» y
 *   elegir el día, y lo demás no existe para el servidor.
 * - **Cambió** es lo que mantiene el historial legible. Marcar un campo y dejarlo como estaba no
 *   es una corrección, y guardarlo dejaría en `canje_corrections` filas «X → X» que ensucian
 *   justamente el rastro que hace legítimo corregir.
 *
 * Un campo marcado y dejado VACÍO sí viaja, como cadena vacía: es la forma de borrar un dato, y
 * el servidor la traduce a NULL desde la 0057. Se excluyen `fecha` y `pdv_id`, que son NOT NULL
 * en la base (ver `NO_VACIABLES`).
 */
export function cambiosDe(
  r: CanjeRow,
  f: FormCorreccion,
  marcados: ReadonlySet<CampoCorreccion>,
  fotos: FotosDeCorreccion = {},
): Record<string, string> {
  const original = formDesde(r);
  const marcado = (c: CampoCorreccion): boolean => marcados.has(c);

  // La fecha que se usa para reconstruir `ocurrido_en` sale del formulario SOLO si se marcó
  // «Fecha»; si no, del registro. Sin esto, corregir únicamente la hora en una pantalla donde el
  // control de fecha quedó deshabilitado con otro valor movería el día sin que nadie lo pidiera.
  const fechaEfectiva = marcado('fecha') && f.fecha !== '' ? f.fecha : r.fecha;

  const out: Record<string, string> = marcado('fecha') ? { ...cambiosDeFecha(r, f.fecha) } : {};

  // Va DESPUÉS de `cambiosDeFecha` a propósito: si además se tocó la hora, esta pisa la que
  // aquella había arrastrado. Al revés, cambiar fecha y hora a la vez perdería la hora nueva.
  if (marcado('horaDeclarada') && f.horaDeclarada !== original.horaDeclarada) {
    // Vaciarla ahora SÍ es una corrección, y antes no podía serlo: sin casilla, un campo de hora
    // en blanco era indistinguible de "no lo toqué". Marcándolo, dejarlo vacío quiere decir
    // "este registro no declara ninguna hora" y `ocurrido_en` queda NULL.
    out.ocurrido_en = f.horaDeclarada === ''
      ? ''
      : `${fechaEfectiva}T${f.horaDeclarada}:00${LIMA_OFFSET}`;
  }
  if (marcado('clienteNombre') && f.clienteNombre !== original.clienteNombre) {
    out.cliente_nombre = f.clienteNombre;
  }
  if (marcado('clienteContacto') && f.clienteContacto !== original.clienteContacto) {
    out.cliente_contacto = f.clienteContacto;
  }
  if (marcado('montoCompra') && f.montoCompra !== original.montoCompra) {
    out.monto_compra = f.montoCompra;
  }
  if (marcado('observaciones') && f.observaciones !== original.observaciones) {
    out.observaciones = f.observaciones;
  }
  // `!== ''` sobrevive a la casilla: `pdv_id` es NOT NULL, así que vaciarlo no borra la tienda,
  // hace que el servidor rechace la tanda entera.
  if (marcado('pdvId') && f.pdvId !== original.pdvId && f.pdvId !== '') out.pdv_id = f.pdvId;
  // Se recorta antes de comparar: un espacio al borde no es una corrección, y dejarlo pasar
  // llenaría el historial de cambios donde el número quedó igual.
  if (marcado('boletaNumero') && f.boletaNumero.trim() !== original.boletaNumero) {
    out.boleta_numero = f.boletaNumero.trim();
  }

  // Las fotos no tienen casilla: elegir un archivo YA es la intención de cambiarla, y una casilla
  // aparte solo podría contradecirla.
  //
  // La comparación contra la ruta actual es un CINTURÓN, no un caso que hoy pueda ocurrir:
  // `subirFotoDeCorreccion` acuña un `crypto.randomUUID()` por llamada, así que la ruta nueva nunca
  // coincide con la vieja y esta condición es verdadera por construcción. Se deja porque
  // `cambiosDe` es pura y exportada, y su contrato —"no viaja nada que no haya cambiado"— vale para
  // los ocho campos de arriba; que las fotos fueran la única excepción sería una trampa para el
  // próximo que las llame desde otro sitio. Si alguna vez la subida pasa a reusar ruta, esta línea
  // es lo que impide una fila «X → X» en el historial.
  if (fotos.boleta !== undefined && fotos.boleta !== r.fotoBoletaPath) {
    out.foto_boleta_path = fotos.boleta;
  }
  if (fotos.premio !== undefined && fotos.premio !== r.fotoPremioPath) {
    out.foto_premio_path = fotos.premio;
  }
  return out;
}

/**
 * Las rutas de las fotos de ESTA corrección: reusa lo que ya se subió y sube solo lo que falta.
 *
 * Existe por el reintento. Las fotos suben ANTES de la RPC —si la subida falla, el registro queda
 * como estaba en vez de apuntar a un objeto que no existe—, pero cuando lo que falla es la RPC
 * (RLS, motivo vacío, red) el objeto ya está en el bucket sin que nadie lo referencie y el modal
 * sigue abierto. Sin esta función, volver a pulsar «Guardar» subía OTRA copia bajo otro uuid, y
 * cada reintento dejaba una huérfana más: Supabase no permite borrar del Storage por SQL, así que
 * lo que se multiplica no se puede limpiar después.
 *
 * Recibe `subir` en vez de llamar a la API: subir es la única parte falible y con red, y dejarla
 * afuera hace que esta decisión —qué se sube y qué se reusa— se pueda probar sin montar nada.
 */
export async function resolverFotosDeCorreccion(
  elegidas: { boleta: File | null; premio: File | null },
  yaSubidas: FotosDeCorreccion,
  subir: (tipo: 'boleta' | 'premio', archivo: File) => Promise<string>,
): Promise<FotosDeCorreccion> {
  const out: FotosDeCorreccion = { ...yaSubidas };
  if (elegidas.boleta !== null && out.boleta === undefined) {
    out.boleta = await subir('boleta', elegidas.boleta);
  }
  if (elegidas.premio !== null && out.premio === undefined) {
    out.premio = await subir('premio', elegidas.premio);
  }
  return out;
}

export default function CanjesBandeja() {
  const { roles } = useAuth();
  /** Puerta de la PANTALLA. La de cada fila es `puedeAccionarFila`, que sí mira la campaña. */
  const pantallaEditable = puedeEditar('canjes', roles);
  const esAdmin = roles.includes('admin');
  /** El validador de datos (0061): acciona en TODAS las campañas, igual que el servidor le permite. */
  const esValidador = roles.includes('datos');
  const soloTiendas = soloSupervisaTiendas(roles);
  const [campaniasCoordinadas, setCampaniasCoordinadas] = useState<Set<string>>(new Set());

  const hoy = formatInTimeZone(new Date(), TZ, 'yyyy-MM-dd');
  const inicioDeMes = hoy.slice(0, 8) + '01';

  const [campanias, setCampanias] = useState<CampaignOption[]>([]);
  const [pdvsDeCampania, setPdvsDeCampania] = useState<PdvOption[]>([]);
  const [dinamicas, setDinamicas] = useState<DinamicaOption[]>([]);
  const [recursos, setRecursos] = useState<WorkerOption[]>([]);

  const [campaignId, setCampaignId] = useState('');
  const [desde, setDesde] = useState(inicioDeMes);
  const [hasta, setHasta] = useState(hoy);
  const [pdvId, setPdvId] = useState('');
  const [workerId, setWorkerId] = useState('');
  const [dynamicId, setDynamicId] = useState('');
  const [verAnulados, setVerAnulados] = useState(false);
  const [soloFuera, setSoloFuera] = useState(false);
  const [soloTardio, setSoloTardio] = useState(false);
  const [soloSinAsist, setSoloSinAsist] = useState(false);
  const [soloAMano, setSoloAMano] = useState(false);

  /** Modal de carga a mano (0374) y cuántos se cargaron con él abierto: al cerrar, si hubo, se relee. */
  const [cargandoAMano, setCargandoAMano] = useState(false);
  const [cargadosAMano, setCargadosAMano] = useState(0);
  /** El turno desde el que se abrió la carga en la vista «Por turno». `null` = el botón general. */
  const [turnoFijo, setTurnoFijo] = useState<TurnoFijo | null>(null);

  /**
   * «Por turno» (0375): una fila por jornada programada, tenga canjes o no. Es la vista por defecto porque es la que
   * deja ver lo que falta, que es lo que pidió el usuario (2-nov). «Por canje» es la lista de siempre.
   */
  const [vista, setVista] = useState<'turno' | 'canje'>('turno');
  const [jornadas, setJornadas] = useState<JornadaCanje[]>([]);
  const [soloPendientes, setSoloPendientes] = useState(false);

  const [filas, setFilas] = useState<CanjeRow[]>([]);
  const [buscado, setBuscado] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [mensaje, setMensaje] = useState('');

  const [corrigiendo, setCorrigiendo] = useState<CanjeRow | null>(null);
  const [form, setForm] = useState<FormCorreccion | null>(null);
  /** Qué campos se van a cambiar. Arranca VACÍO: nada se toca hasta que alguien lo marque. */
  const [camposMarcados, setCamposMarcados] = useState<Set<CampoCorreccion>>(new Set());
  const [fotoBoletaNueva, setFotoBoletaNueva] = useState<File | null>(null);
  const [fotoPremioNueva, setFotoPremioNueva] = useState<File | null>(null);
  /**
   * Lo que YA se subió al bucket en un intento anterior de esta misma corrección.
   *
   * Vive en el estado y no en una variable local porque su razón de ser es sobrevivir a un
   * guardado que falló: la subida entró, la RPC no, y el modal sigue abierto. Ver
   * `resolverFotosDeCorreccion`.
   */
  const [fotosSubidas, setFotosSubidas] = useState<FotosDeCorreccion>({});
  const [motivo, setMotivo] = useState('');
  const [anulando, setAnulando] = useState<CanjeRow | null>(null);
  const [historial, setHistorial] = useState<{ fila: CanjeRow; items: CorreccionRow[] } | null>(null);

  /** Ids marcados para cambiarles la fecha en lote. */
  const [seleccion, setSeleccion] = useState<Set<string>>(new Set());
  const [refechando, setRefechando] = useState(false);
  const [nuevaFecha, setNuevaFecha] = useState('');

  /**
   * RLS: el coordinador solo recibe las campañas de su equipo; el admin, todas.
   *
   * Y se filtran las ARCHIVADAS y las de prueba. Esta consulta no filtraba nada, así que el
   * desplegable ofrecía 12 campañas —Downy, Gillette, Pringles ×4, Softys, dos DEMO…— de las
   * cuales 10 están archivadas o son de banco de pruebas. Reportado por el usuario: «salen varias
   * campañas que ya hemos quitado». Con el filtro quedan las 2 vivas.
   *
   * NO se filtra por periodo, a diferencia de Validación o Cierres (BITACORA §40): esta es una
   * bandeja histórica, y una campaña que terminó tiene que seguir siendo consultable para revisar
   * lo que se registró en ella.
   */
  useEffect(() => {
    void supabase
      .from('campaigns')
      .select('id, client, brand, description')
      .is('archived_at', null)
      .eq('es_banco_pruebas', false)
      .order('client')
      .order('brand')
      .then(({ data }) => setCampanias((data ?? []) as CampaignOption[]));
    // El admin y el validador no necesitan la lista: pueden con todas, y pedirla sería una
    // consulta al aire (mismo criterio que la bandeja de levantamiento).
    if (!esAdmin && !esValidador) void campaniasQueCoordino().then(setCampaniasCoordinadas);
  }, [esAdmin, esValidador]);

  // Las listas dependientes se recargan al cambiar de campaña, y se limpia lo que ya no aplica:
  // dejar seleccionada una tienda de otra campaña devuelve cero filas sin decir por qué.
  useEffect(() => {
    setPdvId('');
    setDynamicId('');
    if (campaignId === '') { setPdvsDeCampania([]); setDinamicas([]); return; }

    void supabase
      .from('campaign_schedules')
      .select('pdv:pdv_id(id, code, name)')
      .eq('campaign_id', campaignId)
      .then(({ data }) => {
        const vistos = new Map<string, PdvOption>();
        for (const fila of (data ?? []) as unknown as { pdv: PdvOption | null }[]) {
          if (fila.pdv) vistos.set(fila.pdv.id, fila.pdv);
        }
        setPdvsDeCampania([...vistos.values()].sort((a, b) => a.name.localeCompare(b.name, 'es')));
      });

    void supabase
      .from('campaign_dynamics')
      .select('id, code, name')
      .eq('campaign_id', campaignId)
      .order('sort')
      .then(({ data }) => setDinamicas((data ?? []) as DinamicaOption[]));
  }, [campaignId]);

  /**
   * Los recursos del desplegable salen de lo YA leído, no de una consulta a `profiles`.
   *
   * Es la lista que de verdad sirve —quien registró algo en el rango— y evita pedir la nómina
   * completa: `select('*')` sobre `profiles` rebota a propósito (42501) por las columnas
   * bancarias, y acá no hace falta ni un dato más que el nombre.
   */
  useEffect(() => {
    const vistos = new Map<string, WorkerOption>();
    for (const f of filas) {
      if (f.worker) {
        vistos.set(f.worker.id, {
          id: f.worker.id,
          nombre: `${f.worker.firstNames} ${f.worker.lastNames}`,
        });
      }
    }
    // En «Por turno» también las personas de los turnos: si no, quien todavía no tiene canjes —justo a quien hay que
    // perseguir— no se podría elegir en el filtro.
    for (const j of jornadas) {
      if (j.asignacion && !vistos.has(j.asignacion.workerId)) {
        vistos.set(j.asignacion.workerId, { id: j.asignacion.workerId, nombre: j.asignacion.nombre });
      }
    }
    setRecursos([...vistos.values()].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')));
  }, [filas, jornadas]);

  /**
   * En «Por turno» los filtros de alertas, de dinámica y de «cargados a mano» NO viajan: filtran canjes, y aplicados acá
   * dejarían en cero turnos que sí tienen canjes, que es justo lo que esta vista existe para no mentir. Tienda y
   * persona sí, porque filtran turnos y canjes por igual.
   */
  async function buscar(modo: 'turno' | 'canje' = vista) {
    if (desde > hasta) { setMensaje('El rango de fechas está invertido.'); return; }
    setCargando(true);
    setMensaje('');
    try {
      const porTurno = modo === 'turno';
      const [r, js] = await Promise.all([
        listCanjesBandeja({
          desde, hasta,
          campaignId: campaignId || null,
          pdvId: pdvId || null,
          workerId: workerId || null,
          dynamicId: porTurno ? null : dynamicId || null,
          estado: verAnulados ? null : 'registrado',
          soloFueraDeProgramacion: !porTurno && soloFuera,
          soloRegistroTardio: !porTurno && soloTardio,
          soloSinAsistencia: !porTurno && soloSinAsist,
          soloCargadosAMano: !porTurno && soloAMano,
        }),
        porTurno ? jornadasParaCanjes(campaignId || null, desde, hasta) : Promise.resolve([]),
      ]);
      setFilas(r);
      setJornadas(js.filter((j) => (pdvId === '' || j.pdv.id === pdvId)
        && (workerId === '' || j.asignacion?.workerId === workerId)));
      setSeleccion(new Set()); // la lista cambió: una selección vieja apuntaría a filas que ya no están
      setBuscado(true);
      if (porTurno ? js.length === 0 && r.length === 0 : r.length === 0) {
        setMensaje(porTurno ? 'No hay turnos ni canjes con esos filtros.' : 'No hay canjes con esos filtros.');
      }
    } catch (e) {
      setMensaje('Error al leer los canjes: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setCargando(false);
    }
  }

  /**
   * El formulario se PRECARGA con los valores actuales aunque nada esté marcado.
   *
   * No es contradictorio con el opt-in: los controles nacen deshabilitados, y lo que muestran es
   * "qué dice hoy este registro", que es la información que hace falta para decidir si hay algo
   * que corregir. Un modal de corrección con los campos en blanco obligaría a cerrar, mirar la
   * fila y volver a abrir.
   */
  function abrirCorreccion(r: CanjeRow) {
    setCorrigiendo(r);
    setForm(formDesde(r));
    setCamposMarcados(new Set());
    setFotoBoletaNueva(null);
    setFotoPremioNueva(null);
    setFotosSubidas({});
    setMotivo('');
  }

  function cerrarCorreccion() {
    setCorrigiendo(null);
    setForm(null);
    setCamposMarcados(new Set());
    setFotoBoletaNueva(null);
    setFotoPremioNueva(null);
    setFotosSubidas({});
  }

  /**
   * Marca o desmarca un campo. Al DESMARCAR se repone su valor desde el registro.
   *
   * Reponerlo no es cosmética: el aviso de arriba promete que los campos sin marcar "quedan
   * deshabilitados mostrando lo que dice hoy el registro". Sin esto, marcar «Cliente», escribir
   * «Pedro» y desmarcar dejaba el input apagado mostrando «Pedro» mientras el registro decía otra
   * cosa. `cambiosDe` lo descarta, así que nunca se guardó mal — pero el coordinador quedaba
   * mirando un valor que no era ni el del registro ni el que se iba a guardar, en la única
   * pantalla cuyo trabajo es decidir si hay algo que corregir.
   */
  function alternarCampo(campo: CampoCorreccion, marcar: boolean) {
    setCamposMarcados((prev) => {
      const next = new Set(prev);
      if (marcar) next.add(campo); else next.delete(campo);
      return next;
    });
    if (!marcar && corrigiendo !== null) {
      setForm((prev) => (prev === null ? prev : reponerCampo(corrigiendo, prev, campo)));
    }
  }

  /**
   * Elegir (o quitar) el archivo de una de las dos fotos.
   *
   * Cambiar de archivo INVALIDA lo que se hubiera subido antes para ese espacio: si no se
   * limpiara, un reintento después de cambiar de foto reusaría la ruta de la anterior y guardaría
   * una evidencia que nadie eligió.
   */
  function elegirFoto(cual: 'boleta' | 'premio', archivo: File | null) {
    if (cual === 'boleta') setFotoBoletaNueva(archivo); else setFotoPremioNueva(archivo);
    setFotosSubidas((prev) => ({ ...prev, [cual]: undefined }));
  }

  async function guardarCorreccion() {
    if (!corrigiendo || !form) return;
    const m = motivo.trim();
    if (bloqueoCorreccion !== null) return;
    setCargando(true);
    try {
      // Las fotos se suben ANTES de armar el diff y antes de llamar a la RPC: si la subida falla,
      // el registro queda exactamente como estaba en vez de apuntar a un objeto que no existe.
      // Se comprimen como en el resto de la app —una foto de celular sin comprimir son varios MB
      // por evidencia— y la compresión que falla devuelve el original, nunca corta el guardado.
      //
      // Lo ya subido se GUARDA en el estado antes de llamar a la RPC: si la RPC falla, el reintento
      // reusa esa ruta en vez de subir otra copia. Ver `resolverFotosDeCorreccion`.
      const fotos = await resolverFotosDeCorreccion(
        { boleta: fotoBoletaNueva, premio: fotoPremioNueva },
        fotosSubidas,
        async (tipo, archivo) => subirFotoDeCorreccion(tipo, await compressImage(archivo)),
      );
      setFotosSubidas(fotos);

      const cambios = cambiosDe(corrigiendo, form, camposMarcados, fotos);
      if (Object.keys(cambios).length === 0) {
        setMensaje('Marcaste campos pero ninguno quedó con un valor distinto: no hay nada que corregir.');
        return;
      }
      await corregirCanje(corrigiendo.id, cambios, m);
      cerrarCorreccion();
      await buscar();
      setMensaje('Corrección guardada. Queda el valor anterior, quién y por qué.');
    } catch (e) {
      setMensaje('Error al corregir: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setCargando(false);
    }
  }

  /**
   * Se relee solo si hubo cargas y ya había una búsqueda a la vista: abrir y cerrar el modal sin
   * cargar nada no tiene por qué mover la lista.
   */
  async function cerrarCargaAMano() {
    setCargandoAMano(false);
    setTurnoFijo(null);
    if (cargadosAMano === 0) return;
    if (buscado) await buscar();
    setMensaje(`${cargadosAMano} canje(s) cargados a mano. Quedan marcados «Cargado a mano», con `
      + 'quién los cargó y el motivo.');
  }

  /** Cambiar de vista relee si ya había una búsqueda: las dos vistas no leen lo mismo (ver `buscar`). */
  function cambiarVista(v: 'turno' | 'canje') {
    if (v === vista) return;
    setVista(v);
    setSeleccion(new Set());
    if (buscado) void buscar(v);
  }

  /** «+ Cargar canje» de un turno: el formulario abre con su campaña, fecha, tienda y persona puestas. */
  function abrirCargaDeTurno(j: JornadaCanje) {
    if (j.asignacion === null) return;
    const [, m, d] = j.fecha.split('-');
    const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'set', 'oct', 'nov', 'dic'];
    setTurnoFijo({
      campaignId: j.campaignId, fecha: j.fecha, pdvId: j.pdv.id, workerId: j.asignacion.workerId,
      descripcion: `${Number(d)}-${MESES[Number(m) - 1]} · ${j.pdv.name} · ${j.asignacion.nombre} `
        + `(${j.inicio} a ${j.fin})`,
    });
    setCargadosAMano(0);
    setCargandoAMano(true);
  }

  function alternarSeleccion(id: string) {
    setSeleccion((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function alternarTodas(marcar: boolean) {
    setSeleccion(marcar ? new Set(refechables.map((f) => f.id)) : new Set());
  }

  /**
   * Cambia SOLO la fecha de los registros marcados, uno por uno.
   *
   * Van de a uno y no en una sola llamada a propósito: `corregir_canje` deja el valor anterior,
   * el motivo y el autor por registro, y ese rastro por fila es lo que permite deshacer una
   * corrección equivocada sin tocar las demás. Si una falla, las anteriores ya quedaron
   * guardadas y el mensaje dice cuántas entraron: es preferible a perder el lote entero.
   */
  async function guardarNuevaFecha() {
    const m = motivo.trim();
    if (nuevaFecha === '' || m === '') return;
    const objetivo = refechables.filter((f) => seleccion.has(f.id));
    if (objetivo.length === 0) return;
    setCargando(true);
    let hechos = 0;
    try {
      for (const f of objetivo) {
        const cambios = cambiosDeFecha(f, nuevaFecha);
        // Un registro que ya estaba en esa fecha no produce cambios: se salta en vez de
        // mandarlo, para no ensuciar el historial con correcciones donde nada pasó.
        if (Object.keys(cambios).length === 0) continue;
        await corregirCanje(f.id, cambios, m);
        hechos += 1;
      }
      setRefechando(false);
      setSeleccion(new Set());
      await buscar();
      setMensaje(
        hechos === 0
          ? 'Ninguno cambió: ya estaban todos en esa fecha.'
          : `${hechos} canje(s) movidos al ${nuevaFecha}. Queda la fecha anterior, quién y por qué.`,
      );
    } catch (e) {
      setMensaje(
        `Se movieron ${hechos} de ${objetivo.length} y falló el siguiente: `
        + (e instanceof Error ? e.message : String(e)),
      );
    } finally {
      setCargando(false);
    }
  }

  /**
   * Descarga en Excel lo que está a la vista. `filas` YA es el resultado filtrado: todos los
   * filtros de esta pantalla viajan a la consulta, así que no hay una lista más larga detrás.
   */
  /** Los metadatos de cabecera del reporte. Los comparten el Excel y el HTML. */
  function metaDelReporte() {
    const filtros: string[] = [];
    if (pdvId !== '') filtros.push(`Tienda: ${pdvsDeCampania.find((p) => p.id === pdvId)?.name ?? pdvId}`);
    if (dynamicId !== '') filtros.push(`Dinámica: ${dinamicas.find((d) => d.id === dynamicId)?.name ?? dynamicId}`);
    if (workerId !== '') filtros.push(`Recurso: ${recursos.find((w) => w.id === workerId)?.nombre ?? workerId}`);
    if (soloFuera) filtros.push('Solo fuera de programación');
    if (soloTardio) filtros.push('Solo registro tardío');
    if (soloSinAsist) filtros.push('Solo sin asistencia');
    if (soloAMano) filtros.push('Solo cargados a mano');
    filtros.push(verAnulados ? 'Incluye anulados' : 'Sin anulados');
    return {
      campaignName: campaignId === '' ? 'Todas las campañas' : nombreCampania(campaignId),
      periodStart: desde,
      periodEnd: hasta,
      filtros,
    };
  }

  async function descargarReporte() {
    if (filas.length === 0) { setMensaje('No hay nada que descargar con esos filtros.'); return; }
    const meta = metaDelReporte();
    setCargando(true);
    setMensaje('Firmando los enlaces de las fotos…');
    try {
      const enlaces = await firmarFotosParaReporte(
        filas.flatMap((f) => [f.fotoBoletaPath, f.fotoPremioPath])
          .filter((p): p is string => p !== null),
      );
      descargarXlsx(buildCanjesWorkbook(meta, filas, enlaces), reporteFileName('canjes', meta));
      setMensaje(
        'Excel descargado. Los enlaces de foto abren sin iniciar sesión y vencen en un año: '
        + 'el archivo lleva evidencia de la operación, trátalo como reservado.',
      );
    } catch (e) {
      setMensaje('No se pudo generar el Excel: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setCargando(false);
    }
  }

  /**
   * HTML con las fotos ADENTRO. Es lo que se manda a quien no tiene usuario del sistema: se
   * abre con doble clic, sin internet y sin sesión, y no deja ninguna puerta viva contra el
   * bucket — a diferencia del Excel, cuyos enlaces sí son llaves a esos objetos.
   */
  async function descargarReporteHtml() {
    if (filas.length === 0) { setMensaje('No hay nada que descargar con esos filtros.'); return; }
    // El HTML es POR CAMPAÑA y se corta ANTES de bajar las fotos: con «Todas las campañas» el
    // archivo saldría con canjes de varias marcas dentro, y es un documento que se le manda a un
    // cliente de marca. El generador tambien lo rechaza (ReporteMulticampaniaError), pero avisar
    // acá evita bajar decenas de MB de fotos para nada.
    const campanias = new Set(filas.map((f) => f.campaignId));
    if (campanias.size > 1) {
      setMensaje(
        `Estas viendo ${campanias.size} campañas juntas. El reporte HTML se genera por campaña: `
        + 'elige una campaña arriba antes de descargar.',
      );
      return;
    }
    const meta = metaDelReporte();
    const rutas = rutasDeFotoDeTodos(filas);
    setCargando(true);
    try {
      const fotos = await descargarFotosEmbebidas(rutas, (hechas, total) => {
        setMensaje(`Incluyendo las fotos en el archivo… ${hechas} de ${total}`);
      });
      descargarHtml(buildCanjesHtml(meta, filas, fotos), reporteCanjesHtmlFileName(meta));
      const faltan = rutas.length - fotos.size;
      setMensaje(
        `HTML descargado con ${fotos.size} de ${rutas.length} fotos adentro`
        + (faltan > 0 ? ` (${faltan} no se pudieron bajar; el archivo lo dice donde va cada una)` : '')
        + '. Se abre sin sesión: trátalo como información reservada.',
      );
    } catch (e) {
      setMensaje('No se pudo generar el HTML: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setCargando(false);
    }
  }

  /**
   * El MISMO reporte, pero abierto en el dialogo de impresion para guardarlo como PDF.
   *
   * No hay un segundo generador ni una libreria de PDF: el motor que compone el PDF es el mismo
   * navegador que ya pinta el reporte, asi que no existe una segunda interpretacion del CSS que
   * pueda equivocarse, y en el PDF el texto sigue siendo texto --se puede buscar un numero de
   * boleta, que es el proposito del documento--.
   *
   * El corte que se imprime es el de los filtros de ESTA pantalla. Los filtros que el archivo
   * lleva adentro sirven para quien lo recibe; aca no se aplican porque el documento se imprime
   * recien generado.
   */
  async function descargarReportePdf() {
    if (filas.length === 0) { setMensaje('No hay nada que imprimir con esos filtros.'); return; }
    const campanias = new Set(filas.map((f) => f.campaignId));
    if (campanias.size > 1) {
      setMensaje(
        `Estas viendo ${campanias.size} campañas juntas. El reporte se genera por campaña: `
        + 'elige una campaña arriba antes de imprimir.',
      );
      return;
    }
    const meta = metaDelReporte();
    const rutas = rutasDeFotoDeTodos(filas);
    setCargando(true);
    try {
      const fotos = await descargarFotosEmbebidas(rutas, (hechas, total) => {
        setMensaje(`Preparando el PDF, incluyendo las fotos… ${hechas} de ${total}`);
      });
      setMensaje('Abriendo el diálogo de impresión…');
      await imprimirHtml(buildCanjesHtml(meta, filas, fotos));
      setMensaje(
        'En el diálogo elige «Guardar como PDF» como destino. '
        + 'El PDF lleva evidencia de la operación: trátalo como información reservada.',
      );
    } catch (e) {
      setMensaje('No se pudo preparar el PDF: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setCargando(false);
    }
  }

  async function confirmarAnulacion() {
    if (!anulando) return;
    const m = motivo.trim();
    if (m === '') return;
    setCargando(true);
    try {
      await anularCanje(anulando.id, m);
      setAnulando(null);
      await buscar();
      setMensaje('Canje anulado. No se borró: queda visible como anulado.');
    } catch (e) {
      setMensaje('Error al anular: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setCargando(false);
    }
  }

  async function abrirHistorial(r: CanjeRow) {
    setCargando(true);
    try {
      setHistorial({ fila: r, items: await listCorreccionesDeCanje(r.id) });
    } catch (e) {
      setMensaje('No se pudo leer el historial: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setCargando(false);
    }
  }

  async function verFoto(path: string) {
    try {
      window.open(await getPhotoUrl(path), '_blank');
    } catch {
      setMensaje('No se pudo obtener la foto.');
    }
  }

  const hayFotoNueva = fotoBoletaNueva !== null || fotoPremioNueva !== null;
  // `form ?? undefined`: sin modal abierto no hay formulario que revisar, y el bloqueo se calcula
  // igual (el botón que lo usa tampoco está en pantalla).
  const bloqueoCorreccion = bloqueoDeCorreccion(
    camposMarcados, hayFotoNueva, motivo, form ?? undefined,
  );

  const refechables = filasRefechables(esAdmin, esValidador, campaniasCoordinadas, filas);
  const todasMarcadas = refechables.length > 0 && refechables.every((f) => seleccion.has(f.id));
  const marcadas = refechables.filter((f) => seleccion.has(f.id)).length;

  const totalCanjes = filas.reduce((s, f) => s + f.totalCanjes, 0);
  const conBandera = filas.filter(
    (f) => f.fueraDeProgramacion || f.registroTardio || f.sinAsistencia,
  ).length;
  const nombreCampania = (id: string) => {
    const c = campanias.find((x) => x.id === id);
    return c ? `${c.client} — ${c.brand}` : '—';
  };
  /**
   * Dónde se puede cargar a mano: la misma puerta por campaña que corregir (`puedeAccionarFila`),
   * que es la que repite el servidor en `puede_cargar_canje`. El supervisor de ruta mira y no carga.
   */
  const campaniasParaCargar = campanias.filter(
    (c) => puedeAccionarFila(esAdmin, esValidador, campaniasCoordinadas, c.id),
  );
  const puedeCargar = pantallaEditable && !soloTiendas && campaniasParaCargar.length > 0;
  const cargadasAMano = filas.filter((f) => f.origen === 'bandeja').length;
  const agrupado = agruparCanjesPorTurno(jornadas, filas);

  /**
   * Historial, corregir, anular y borrar de UN canje. Una sola copia para la lista por canje y para la vista por
   * turno: si cada vista tuviera la suya, la regla de quién puede qué terminaría distinta en una de las dos.
   */
  function acciones(f: CanjeRow) {
    return (
      <>
        <button type="button" onClick={() => void abrirHistorial(f)}
          className="block text-xs text-blue-600 underline">Historial</button>
        {pantallaEditable
          && puedeAccionarFila(esAdmin, esValidador, campaniasCoordinadas, f.campaignId)
          && f.estado === 'registrado' && (
          <>
            <button type="button" onClick={() => abrirCorreccion(f)}
              className="block text-xs text-blue-600 underline">Corregir</button>
            <button type="button"
              onClick={() => { setMotivo(''); setAnulando(f); }}
              className="block text-xs text-red-600 underline">Anular</button>
            {/* «Anular» y «Borrar» conviven a proposito y NO son lo mismo: anular deja
                el canje a la vista con su estado y su rastro —sirve cuando paso algo y
                hay que explicarlo—, y borrar lo saca de la base porque fue un error
                total. El usuario pidio la segunda: *«preferiria borrado real porque no
                deberia haber rastro, es error total»*. */}
            <span className="mt-1 block">
              <BorrarRegistro
                sujeto={`Canje de ${f.clienteNombre ?? 'cliente sin nombre'}
                  · ${f.pdv?.name ?? 'sin tienda'} · ${f.fecha}`}
                queSePierde="sus lineas de premio y su historial de correcciones"
                onBorrar={(motivo) => borrarCanje(f.id, motivo)}
                onBorrado={() => void buscar()}
              />
            </span>
          </>
        )}
      </>
    );
  }

  return (
    // Sin padding propio: lo pone `BandejaDeFormularios` para las tres solapas. Cuando lo tenía
    // acá, pasar de «Canjes» a «Cierres de turno» corría el contenido 24 px y la pantalla parecía
    // saltar.
    <div>
      {/* `h2` y no `h1`: el `h1` de la pantalla es «Registros de campo», el nombre de la sección.
          Dos `h1` seguidos dejan a quien navega por encabezados sin saber cuál contiene a cuál. */}
      <div className="mb-1 flex flex-wrap items-start justify-between gap-2">
        <h2 className="text-base font-semibold text-gray-900">Bandeja de canjes</h2>
        {puedeCargar && (
          <button type="button"
            onClick={() => { setTurnoFijo(null); setCargadosAMano(0); setCargandoAMano(true); }}
            className="rounded bg-blue-600 px-4 py-1.5 text-sm text-white">
            Cargar canje a mano
          </button>
        )}
      </div>
      <p className="mb-4 max-w-3xl text-sm text-gray-600">
        Lo que el campo registró, por rango de fechas. El tablero de <strong>Hoy</strong> solo
        muestra el día en curso; acá aparecen también los de regularización, que llegan días
        después de la fecha que declaran.
        {puedeCargar && (
          <> Lo que no se registró desde el celular se carga con <strong>Cargar canje a mano</strong>,
            a nombre de la persona que lo hizo y con el motivo.</>
        )}
      </p>

      {/* Dos lecturas de lo mismo. «Por turno» es la que deja ver lo que falta: una fila por jornada programada,
          con o sin canjes, como «Visitas a puestos». */}
      <div role="group" aria-label="Cómo ver la bandeja" className="mb-4 inline-flex rounded border border-gray-300 text-sm">
        {([['turno', 'Por turno'], ['canje', 'Por canje']] as const).map(([v, etiqueta]) => (
          <button key={v} type="button" aria-pressed={vista === v} onClick={() => cambiarVista(v)}
            className={`px-3 py-1.5 first:rounded-l last:rounded-r ${
              vista === v ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
            {etiqueta}
          </button>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-sm">
          Campaña
          <select className="rounded border px-2 py-1" value={campaignId}
            onChange={(e) => setCampaignId(e.target.value)}>
            <option value="">Todas</option>
            {campanias.map((c) => (
              <option key={c.id} value={c.id}>{c.client} — {c.brand}</option>
            ))}
          </select>
        </label>

        <label className="flex flex-col text-sm">
          Desde
          <input type="date" className="rounded border px-2 py-1" value={desde}
            onChange={(e) => setDesde(e.target.value)} />
        </label>

        <label className="flex flex-col text-sm">
          Hasta
          <input type="date" className="rounded border px-2 py-1" value={hasta}
            onChange={(e) => setHasta(e.target.value)} />
        </label>

        <label className="flex flex-col text-sm">
          Tienda
          <select className="rounded border px-2 py-1" value={pdvId}
            disabled={pdvsDeCampania.length === 0}
            onChange={(e) => setPdvId(e.target.value)}>
            <option value="">Todas</option>
            {pdvsDeCampania.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </label>

        {vista === 'canje' && (
        <label className="flex flex-col text-sm">
          Dinámica
          <select className="rounded border px-2 py-1" value={dynamicId}
            disabled={dinamicas.length === 0}
            onChange={(e) => setDynamicId(e.target.value)}>
            <option value="">Todas</option>
            {dinamicas.map((d) => (
              <option key={d.id} value={d.id}>{d.code} — {d.name}</option>
            ))}
          </select>
        </label>
        )}

        <label className="flex flex-col text-sm">
          Recurso
          <select className="rounded border px-2 py-1" value={workerId}
            disabled={recursos.length === 0}
            onChange={(e) => setWorkerId(e.target.value)}>
            <option value="">Todos</option>
            {recursos.map((w) => (
              <option key={w.id} value={w.id}>{w.nombre}</option>
            ))}
          </select>
        </label>

        <button type="button" onClick={() => void buscar()} disabled={cargando}
          className="rounded bg-blue-600 px-4 py-1.5 text-white disabled:opacity-50">
          {cargando ? 'Buscando…' : 'Buscar'}
        </button>

        {/* El supervisor de ruta mira en pantalla y no descarga (decisión del usuario, 25-set): el
            Excel lleva enlaces a las fotos que abren sin iniciar sesión durante un año. */}
        {!soloTiendas && (
          <>
            <button type="button" onClick={() => void descargarReporte()}
              disabled={cargando || filas.length === 0}
              className="rounded border border-gray-300 px-4 py-1.5 text-sm disabled:opacity-50">
              Descargar Excel ({filas.length})
            </button>

            {/* El HTML es el que se manda afuera: lleva las fotos adentro, así que no depende de
                ningún enlace ni deja puerta abierta contra el bucket. */}
            <button type="button" onClick={() => void descargarReporteHtml()}
              disabled={cargando || filas.length === 0}
              title="Un solo archivo con las fotos adentro. Se abre sin internet y sin iniciar sesión."
              className="rounded border border-gray-300 px-4 py-1.5 text-sm disabled:opacity-50">
              Descargar HTML con fotos ({filas.length})
            </button>

            {/* El PDF sale del mismo reporte, por el dialogo de impresion del navegador. No se agrego
                ninguna libreria: las que convierten HTML a PDF en el cliente rasterizan el texto, y en
                un documento cuyo proposito es auditar boletas eso significa que el numero de boleta
                deja de poder buscarse. */}
            <button type="button" onClick={() => void descargarReportePdf()}
              disabled={cargando || filas.length === 0}
              title="Abre el diálogo de impresión: elige «Guardar como PDF» como destino."
              className="rounded border border-gray-300 px-4 py-1.5 text-sm disabled:opacity-50">
              Guardar como PDF ({filas.length})
            </button>
          </>
        )}
      </div>

      <div className="mb-4 flex flex-wrap gap-x-5 gap-y-2 text-sm">
        {vista === 'turno' && (
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={soloPendientes}
            onChange={(e) => setSoloPendientes(e.target.checked)} />
          Solo pendientes <span className="text-xs text-gray-500">(sin canjes o sin persona)</span>
        </label>
        )}
        {vista === 'canje' && (<>
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={soloFuera}
            onChange={(e) => setSoloFuera(e.target.checked)} />
          Solo fuera de programación
        </label>
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={soloTardio}
            onChange={(e) => setSoloTardio(e.target.checked)} />
          Solo registro tardío
        </label>
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={soloSinAsist}
            onChange={(e) => setSoloSinAsist(e.target.checked)} />
          Solo sin asistencia
        </label>
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={soloAMano}
            onChange={(e) => setSoloAMano(e.target.checked)} />
          Solo cargados a mano
        </label>
        </>)}
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={verAnulados}
            onChange={(e) => setVerAnulados(e.target.checked)} />
          Incluir anulados
        </label>
      </div>

      {mensaje !== '' && (
        <p className="mb-4 rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
          {mensaje}
        </p>
      )}

      {/* Los tres números de la línea de texto pasaron a tarjetas —el usuario pidió los mismos
          indicadores que ya tienen las otras bandejas— y la línea se queda con lo que una tarjeta
          no puede decir: por qué faltan los botones. El número y la explicación no compiten. */}
      {vista === 'canje' && buscado && filas.length > 0 && (
        <TarjetasResumen indicadores={[
          { titulo: 'Registros', valor: filas.length },
          { titulo: 'Canjes', valor: totalCanjes, detalle: 'lo que cuenta el cliente' },
          { titulo: 'Con alerta', valor: conBandera, tono: 'atencion',
            detalle: 'fuera de programación, tardío o sin asistencia' },
          { titulo: 'Anulados', valor: filas.filter((f) => f.estado === 'anulado').length,
            tono: 'malo' },
          { titulo: 'Cargados a mano', valor: cargadasAMano, detalle: 'desde esta bandeja' },
          { titulo: 'Tiendas', valor: new Set(filas.map((f) => f.pdv?.id ?? f.id)).size },
          { titulo: 'Personas', valor: new Set(filas.map((f) => f.worker?.id ?? f.id)).size },
        ]} />
      )}

      {buscado && filas.length > 0 && (!pantallaEditable || (!esAdmin && !esValidador && filas.some(
        (f) => !puedeAccionarFila(esAdmin, esValidador, campaniasCoordinadas, f.campaignId),
      ))) && (
        <p className="mb-3 text-sm text-gray-700">
          {!pantallaEditable && 'Solo lectura.'}
          {/* Se dice por qué faltan los botones, en vez de dejar que parezca un error. El caso real
              es el supervisor: comparte el rol `coordinador` con el coordinador designado, así que
              ve la bandeja completa y solo puede accionar donde él es el designado. */}
          {pantallaEditable && !esAdmin && !esValidador && filas.some(
            (f) => !puedeAccionarFila(esAdmin, esValidador, campaniasCoordinadas, f.campaignId),
          ) && 'Corregir y anular, solo en las campañas que coordinas.'}
        </p>
      )}

      {/* Barra de la acción en lote. Aparece solo con algo marcado: el caso que la motivó es
          una impulsadora que subió el sábado y el domingo con fecha del lunes, y son 17
          registros. De a uno serían 17 modales. */}
      {vista === 'canje' && marcadas > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded border border-blue-200 bg-blue-50 px-3 py-2">
          <span className="text-sm text-blue-900">{marcadas} marcado(s)</span>
          <button type="button"
            onClick={() => { setNuevaFecha(''); setMotivo(''); setRefechando(true); }}
            className="rounded bg-blue-600 px-3 py-1 text-sm text-white">
            Cambiar fecha
          </button>
          <button type="button" onClick={() => setSeleccion(new Set())}
            className="rounded border border-blue-300 px-3 py-1 text-sm text-blue-900">
            Quitar selección
          </button>
        </div>
      )}

      {vista === 'turno' && buscado && (
        <CanjesPorTurno
          jornadas={jornadas}
          agrupado={agrupado}
          hoy={hoy}
          soloPendientes={soloPendientes}
          mostrarCampania={campaignId === ''}
          nombreCampania={nombreCampania}
          puedeCargarEn={(id) => pantallaEditable && !soloTiendas
            && puedeAccionarFila(esAdmin, esValidador, campaniasCoordinadas, id)}
          onCargar={abrirCargaDeTurno}
          renderCanje={(c) => (
            <div className="flex flex-wrap items-start justify-between gap-3 rounded border bg-white px-3 py-2 text-sm">
              <div className="min-w-0">
                {c.lineas.map((l, i) => (
                  <div key={`${c.id}-${l.dynamicId}-${i}`}>
                    <span className="font-medium">{l.canjes}×</span> {l.dynamicName || l.dynamicCode}
                    {l.premios.length > 0 && (
                      <span className="text-xs text-gray-600">
                        {' · '}{l.premios.map((p) => `${p.prizeName} ×${p.units}`).join(' · ')}
                      </span>
                    )}
                  </div>
                ))}
                <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
                  <span>registrado {formatInTimeZone(new Date(c.registradoEn), TZ, 'dd-MM HH:mm')}</span>
                  {c.clienteNombre && <span>· {c.clienteNombre}</span>}
                  {c.boletaNumero && <span className="font-mono">· N.º {c.boletaNumero}</span>}
                  {c.estado === 'anulado' && <Senal tono="grave">Anulado</Senal>}
                  {c.origen === 'bandeja' && (
                    <span title={c.motivoCarga ? `Motivo: ${c.motivoCarga}` : undefined}>
                      <Senal tono="neutro">
                        Cargado a mano{c.cargadoPor ? ` · por ${c.cargadoPor.firstNames} ${c.cargadoPor.lastNames}` : ''}
                      </Senal>
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-start gap-x-3">{acciones(c)}</div>
            </div>
          )}
        />
      )}

      {vista === 'canje' && filas.length > 0 && (
        <div className="overflow-x-auto rounded border">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-600">
              <tr>
                <th className="px-3 py-2">
                  {refechables.length > 0 && (
                    <input type="checkbox"
                      aria-label="Seleccionar todos los canjes de la lista"
                      checked={todasMarcadas}
                      onChange={(e) => alternarTodas(e.target.checked)} />
                  )}
                </th>
                <th className="px-3 py-2">Fecha</th>
                <th className="px-3 py-2">Tienda</th>
                <th className="px-3 py-2">Recurso</th>
                <th className="px-3 py-2">Cliente</th>
                <th className="px-3 py-2">Monto</th>
                <th className="px-3 py-2">Canjes y premios</th>
                <th className="px-3 py-2">Estado</th>
                <th className="px-3 py-2">Evidencia</th>
                <th className="px-3 py-2">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filas.map((f) => (
                <tr key={f.id} className={f.estado === 'anulado' ? 'bg-gray-50 text-gray-500' : ''}>
                  <td className="px-3 py-2 align-top">
                    {/* Sin casilla en lo que no se puede accionar: una casilla que se marca y
                        después no hace nada es peor que no tenerla. */}
                    {refechables.some((x) => x.id === f.id) && (
                      <input type="checkbox" aria-label={`Seleccionar el canje del ${f.fecha}`}
                        checked={seleccion.has(f.id)}
                        onChange={() => alternarSeleccion(f.id)} />
                    )}
                  </td>
                  <td className="px-3 py-2 align-top whitespace-nowrap">
                    {f.fecha}
                    <div className="text-xs text-gray-500">
                      registrado {formatInTimeZone(new Date(f.registradoEn), TZ, 'dd-MM HH:mm')}
                    </div>
                    {f.ocurridoEn && (
                      <div className="text-xs text-gray-500">
                        declarado {formatInTimeZone(new Date(f.ocurridoEn), TZ, 'HH:mm')}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">
                    {f.pdv?.name ?? '—'}
                    <div className="text-xs text-gray-500">{nombreCampania(f.campaignId)}</div>
                  </td>
                  <td className="px-3 py-2 align-top">
                    {f.worker ? `${f.worker.firstNames} ${f.worker.lastNames}` : '—'}
                    {f.worker?.dni && <div className="text-xs text-gray-500">{f.worker.dni}</div>}
                  </td>
                  <td className="px-3 py-2 align-top">
                    {/* `gray-500` y no `gray-400`: a 2,8:1 el «sin nombre» no se lee, y es un dato
                        de la fila —que el canje no trae cliente—, no una decoración. */}
                    {f.clienteNombre || <span className="text-gray-500">sin nombre</span>}
                    {f.clienteContacto && (
                      <div className="text-xs text-gray-500">{f.clienteContacto}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top whitespace-nowrap">
                    {f.montoCompra === null
                      ? <span className="text-gray-500">—</span>
                      : `S/ ${f.montoCompra.toFixed(2)}`}
                  </td>
                  <td className="px-3 py-2 align-top">
                    {f.lineas.map((l, i) => (
                      <div key={`${f.id}-${l.dynamicId}-${i}`} className="mb-1 last:mb-0">
                        <span className="font-medium">{l.canjes}×</span> {l.dynamicName || l.dynamicCode}
                        {l.premios.length > 0 && (
                          <div className="text-xs text-gray-600">
                            {l.premios.map((p) => `${p.prizeName} ×${p.units}`).join(' · ')}
                            {l.premios.some((p) => p.equivalencia) && (
                              <span className="ml-1 inline-block"><Senal>equivalencia</Senal></span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </td>
                  <td className="px-3 py-2 align-top">
                    <div className="flex flex-col items-start gap-1">
                      {f.estado === 'anulado' && <Senal tono="grave">Anulado</Senal>}
                      {/* Lo cargado a mano se distingue siempre (0374): sin esto, en un mes nadie
                          puede decir qué midió el sistema y qué se cargó desde la oficina. */}
                      {f.origen === 'bandeja' && (
                        <span title={f.motivoCarga ? `Motivo: ${f.motivoCarga}` : undefined}>
                          <Senal tono="neutro">Cargado a mano</Senal>
                          {f.cargadoPor && (
                            <span className="mt-0.5 block text-xs text-gray-500">
                              por {f.cargadoPor.firstNames} {f.cargadoPor.lastNames}
                            </span>
                          )}
                        </span>
                      )}
                      {BANDERAS.filter((b) => f[b.key] === true).map((b) => (
                        // El `title` va en un envoltorio porque `Senal` no lo recibe. Sin él, la
                        // bandera queda en dos palabras y lo que significa —«ese día no tenía esa
                        // tienda asignada»— no está escrito en ninguna parte de la pantalla.
                        <span key={b.label} title={b.titulo}>
                          <Senal>{b.label}</Senal>
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2 align-top">
                    {/* El número de boleta va PRIMERO: desde la 0043 hay campañas donde es la
                        única evidencia, porque en tienda no dejan usar el celular. */}
                    {f.boletaNumero !== null && (
                      <div className="mb-0.5 font-mono text-xs text-gray-800">
                        N.º {f.boletaNumero}
                      </div>
                    )}
                    {f.boletaNumero === null
                      && f.fotoBoletaPath === null && f.fotoPremioPath === null && (
                      <span className="text-xs text-gray-500">sin evidencia</span>
                    )}
                    {f.fotoBoletaPath && (
                      <button type="button" onClick={() => void verFoto(f.fotoBoletaPath!)}
                        className="block text-xs text-blue-600 underline">boleta</button>
                    )}
                    {f.fotoPremioPath && (
                      <button type="button" onClick={() => void verFoto(f.fotoPremioPath!)}
                        className="block text-xs text-blue-600 underline">premio</button>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top whitespace-nowrap">
                    {acciones(f)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {cargandoAMano && (
        <Modal titulo="Cargar canje a mano" onCerrar={() => void cerrarCargaAMano()}>
          <CargaManualCanje
            campanias={campaniasParaCargar}
            campaignIdInicial={campaignId}
            fijo={turnoFijo ?? undefined}
            onCargado={() => setCargadosAMano((n) => n + 1)}
            onCerrar={() => void cerrarCargaAMano()} />
        </Modal>
      )}

      {refechando && (
        <Modal titulo={`Cambiar la fecha de ${marcadas} canje(s)`}
          onCerrar={() => setRefechando(false)}>
          <p className="mb-3 text-sm text-gray-600">
            Cambia <strong>solo la fecha</strong>. Todo lo demás —cliente, monto, boleta, premios,
            fotos— queda igual. Si el registro declaraba una hora, se conserva y se mueve al día
            nuevo. El momento en que el servidor lo recibió no se toca: de esa diferencia sale la
            marca de registro tardío, y es lo que prueba cuándo se cargó de verdad.
          </p>
          <div className="grid gap-3 text-sm">
            <label className="flex flex-col">
              Fecha nueva
              <input type="date" className="rounded border px-2 py-1" value={nuevaFecha}
                onChange={(e) => setNuevaFecha(e.target.value)} />
            </label>
            <label className="flex flex-col">
              Motivo <span className="text-xs text-gray-500">(obligatorio)</span>
              <textarea rows={2} className="rounded border px-2 py-1" value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Ej.: los cargó el lunes pero los canjes fueron del sábado" />
            </label>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" onClick={() => setRefechando(false)}
              className="rounded border px-3 py-1.5 text-sm">Cancelar</button>
            <button type="button" onClick={() => void guardarNuevaFecha()}
              disabled={cargando || nuevaFecha === '' || motivo.trim() === ''}
              className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white disabled:opacity-50">
              Cambiar la fecha
            </button>
          </div>
        </Modal>
      )}

      {corrigiendo && form && (
        <Modal titulo="Corregir canje" onCerrar={cerrarCorreccion}>
          <p className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <strong>Marca solo los campos que vas a cambiar.</strong> Los que no marques quedan
            deshabilitados mostrando lo que dice hoy el registro, y no viajan ni aparecen en el
            historial. Un campo marcado y dejado <strong>vacío borra ese dato</strong>.
          </p>
          <p className="mb-3 text-sm text-gray-600">
            El momento en que el servidor lo recibió no se puede modificar: de esa diferencia sale
            la marca de tardío.
          </p>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <CampoCorregible etiqueta={ETIQUETA_CAMPO.fecha} campo="fecha"
              marcados={camposMarcados} sePuedeVaciar={sePuedeVaciar}
              onAlternar={alternarCampo}
              ayuda="El momento declarado se mueve con ella, conservando la hora del día.">
              <input type="date" aria-label="Fecha" className="w-full rounded border px-2 py-1"
                disabled={!camposMarcados.has('fecha')} value={form.fecha}
                onChange={(e) => setForm({ ...form, fecha: e.target.value })} />
            </CampoCorregible>

            <CampoCorregible etiqueta={ETIQUETA_CAMPO.horaDeclarada} campo="horaDeclarada"
              marcados={camposMarcados} sePuedeVaciar={sePuedeVaciar}
              onAlternar={alternarCampo}
              ayuda="Se mueve con la fecha. Vaciarla deja el registro sin momento declarado.">
              <input type="time" aria-label="Hora declarada"
                className="w-full rounded border px-2 py-1"
                disabled={!camposMarcados.has('horaDeclarada')} value={form.horaDeclarada}
                onChange={(e) => setForm({ ...form, horaDeclarada: e.target.value })} />
            </CampoCorregible>

            <CampoCorregible etiqueta={ETIQUETA_CAMPO.pdvId} campo="pdvId" marcados={camposMarcados} sePuedeVaciar={sePuedeVaciar}
              ancho="completo"
              onAlternar={alternarCampo}
              ayuda={pdvsDeCampania.length === 0
                ? 'Filtra por campaña arriba para poder cambiar de tienda.'
                : undefined}>
              <select aria-label="Tienda" className="w-full rounded border px-2 py-1"
                disabled={!camposMarcados.has('pdvId')} value={form.pdvId}
                onChange={(e) => setForm({ ...form, pdvId: e.target.value })}>
                {form.pdvId === '' && <option value="">— sin tienda —</option>}
                {pdvsDeCampania.length === 0 && corrigiendo.pdv && (
                  <option value={corrigiendo.pdv.id}>{corrigiendo.pdv.name}</option>
                )}
                {pdvsDeCampania.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </CampoCorregible>

            <CampoCorregible etiqueta={ETIQUETA_CAMPO.clienteNombre} campo="clienteNombre"
              marcados={camposMarcados} sePuedeVaciar={sePuedeVaciar} onAlternar={alternarCampo}>
              <input aria-label="Cliente" className="w-full rounded border px-2 py-1"
                disabled={!camposMarcados.has('clienteNombre')} value={form.clienteNombre}
                onChange={(e) => setForm({ ...form, clienteNombre: e.target.value })} />
            </CampoCorregible>

            <CampoCorregible etiqueta={ETIQUETA_CAMPO.clienteContacto} campo="clienteContacto"
              marcados={camposMarcados} sePuedeVaciar={sePuedeVaciar} onAlternar={alternarCampo}>
              <input aria-label="Contacto" className="w-full rounded border px-2 py-1"
                disabled={!camposMarcados.has('clienteContacto')} value={form.clienteContacto}
                onChange={(e) => setForm({ ...form, clienteContacto: e.target.value })} />
            </CampoCorregible>

            <CampoCorregible etiqueta={ETIQUETA_CAMPO.montoCompra} campo="montoCompra"
              marcados={camposMarcados} sePuedeVaciar={sePuedeVaciar} onAlternar={alternarCampo}>
              <input type="number" step="0.01" aria-label="Monto en soles"
                className="w-full rounded border px-2 py-1"
                disabled={!camposMarcados.has('montoCompra')} value={form.montoCompra}
                onChange={(e) => setForm({ ...form, montoCompra: e.target.value })} />
            </CampoCorregible>

            <CampoCorregible etiqueta={ETIQUETA_CAMPO.boletaNumero} campo="boletaNumero"
              marcados={camposMarcados} sePuedeVaciar={sePuedeVaciar}
              onAlternar={alternarCampo}>
              <input aria-label="Número de boleta" className="w-full rounded border px-2 py-1"
                autoCapitalize="characters" autoComplete="off" spellCheck={false}
                disabled={!camposMarcados.has('boletaNumero')} value={form.boletaNumero}
                onChange={(e) => setForm({ ...form, boletaNumero: e.target.value })} />
            </CampoCorregible>

            <CampoCorregible etiqueta={ETIQUETA_CAMPO.observaciones} campo="observaciones"
              marcados={camposMarcados} sePuedeVaciar={sePuedeVaciar}
              ancho="completo" onAlternar={alternarCampo}>
              <textarea rows={2} aria-label="Observaciones"
                className="w-full rounded border px-2 py-1"
                disabled={!camposMarcados.has('observaciones')} value={form.observaciones}
                onChange={(e) => setForm({ ...form, observaciones: e.target.value })} />
            </CampoCorregible>
          </div>

          {/* Las fotos van en su propio bloque y sin casilla: elegir un archivo YA dice que se
              quiere cambiar esa foto. */}
          <div className="mt-4 rounded border border-gray-200 p-3">
            <h3 className="text-sm font-medium">Fotos de evidencia</h3>
            <p className="mt-1 text-xs text-gray-600">
              Elegir una foto la <strong>reemplaza</strong>, o la <strong>agrega</strong> donde no
              había. La foto anterior <strong>no se borra</strong>: queda en «Historial», donde se
              abre con un clic, así que la evidencia original se puede seguir mirando.
            </p>
            <div className="mt-2 grid grid-cols-2 gap-3">
              <CampoFotoCorreccion etiqueta="Foto de boleta" pathActual={corrigiendo.fotoBoletaPath}
                elegida={fotoBoletaNueva} onElegir={(f) => elegirFoto('boleta', f)}
                onVer={(p) => void verFoto(p)} />
              <CampoFotoCorreccion etiqueta="Foto de premio" pathActual={corrigiendo.fotoPremioPath}
                elegida={fotoPremioNueva} onElegir={(f) => elegirFoto('premio', f)}
                onVer={(p) => void verFoto(p)} />
            </div>
          </div>

          <label className="mt-3 flex flex-col text-sm">
            Motivo de la corrección <span className="text-xs text-gray-500">(obligatorio)</span>
            <textarea rows={2} className="rounded border px-2 py-1" value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ej.: la impulsadora sumó los 3 del cliente anterior" />
          </label>

          <p className="mt-3 text-xs text-gray-500">
            La cantidad de canjes y los premios de cada línea todavía no se pueden corregir desde
            acá: el servidor solo acepta los campos de arriba.
          </p>
          <div className="mt-4 flex items-center justify-end gap-3">
            {/* El porqué se dice ANTES de pulsar: un botón apagado sin explicación se lee como
                una pantalla rota. */}
            {bloqueoCorreccion !== null && (
              <span className="text-xs text-gray-600">{bloqueoCorreccion}</span>
            )}
            <button type="button" onClick={cerrarCorreccion}
              className="rounded border px-3 py-1.5 text-sm">Cancelar</button>
            <button type="button" onClick={() => void guardarCorreccion()}
              disabled={cargando || bloqueoCorreccion !== null}
              className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white disabled:opacity-50">
              Guardar corrección
            </button>
          </div>
        </Modal>
      )}

      {anulando && (
        <Modal titulo="Anular canje" onCerrar={() => setAnulando(null)}>
          <p className="mb-3 text-sm text-gray-600">
            El registro no se borra: queda visible como anulado, con el motivo y quién lo anuló.
          </p>
          <label className="flex flex-col text-sm">
            Motivo <span className="text-xs text-gray-500">(obligatorio)</span>
            <textarea rows={3} className="rounded border px-2 py-1" value={motivo}
              onChange={(e) => setMotivo(e.target.value)} />
          </label>
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" onClick={() => setAnulando(null)}
              className="rounded border px-3 py-1.5 text-sm">Cancelar</button>
            <button type="button" onClick={() => void confirmarAnulacion()}
              disabled={cargando || motivo.trim() === ''}
              className="rounded bg-red-600 px-3 py-1.5 text-sm text-white disabled:opacity-50">
              Anular
            </button>
          </div>
        </Modal>
      )}

      {historial && (
        <Modal titulo="Historial de cambios" onCerrar={() => setHistorial(null)}>
          <p className="mb-3 text-sm text-gray-600">
            {historial.fila.fecha} · {historial.fila.pdv?.name ?? '—'} ·{' '}
            {historial.fila.clienteNombre || 'sin nombre'}
          </p>
          {historial.items.length === 0 ? (
            <p className="text-sm text-gray-500">
              Este registro no tiene correcciones: está tal como lo reportó el campo.
            </p>
          ) : (
            <ul className="space-y-3 text-sm">
              {historial.items.map((c) => (
                <li key={c.id} className="rounded border px-3 py-2">
                  <div className="font-medium">{CAMPO_LABEL[c.campo] ?? c.campo}</div>
                  <div className="flex flex-wrap items-center gap-1.5 text-gray-700">
                    <ValorDeCorreccion campo={c.campo} valor={c.valorAnterior} cual="anterior"
                      onVer={(p) => void verFoto(p)} />
                    <span aria-hidden="true">→</span>
                    <ValorDeCorreccion campo={c.campo} valor={c.valorNuevo} cual="nuevo"
                      onVer={(p) => void verFoto(p)} />
                  </div>
                  <div className="mt-1 text-xs text-gray-600">Motivo: {c.motivo}</div>
                  <div className="text-xs text-gray-500">
                    {/* Corrigen solo el jefe de operaciones y el coordinador (puede_editar_canje), y el
                        supervisor no lee sus perfiles: para él el autor es «Oficina», no un desconocido. */}
                    {c.actor
                      ? `${c.actor.firstNames} ${c.actor.lastNames}`
                      : soloTiendas ? 'Oficina' : 'actor desconocido'}
                    {' · '}
                    {formatInTimeZone(new Date(c.createdAt), TZ, 'dd-MM-yyyy HH:mm')}
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-4 flex justify-end">
            <button type="button" onClick={() => setHistorial(null)}
              className="rounded border px-3 py-1.5 text-sm">Cerrar</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/**
 * Un valor del historial: el anterior (tachado) o el nuevo (en negrita).
 *
 * **Las rutas de foto se pintan como BOTÓN y no como texto**, y es lo que hace verdadera la promesa
 * con la que se decidió no borrar nada del bucket: *"la foto anterior no se borra, queda en el
 * historial, así que la evidencia original se puede seguir mirando"* (0057, §1). Mientras el
 * historial mostrara `form-photos/canje-….jpg` tachado, esa frase era falsa en la práctica —el
 * objeto seguía en el Storage y desde la pantalla no había ninguna forma de llegar a él—, y quien
 * reemplazaba una foto se quedaba sin poder comprobar qué reportó el campo de verdad.
 *
 * Una ruta vacía o NULL se muestra «sin foto», no como botón: es el caso normal de la corrección
 * que AGREGÓ la primera foto, y un botón que abre la nada es peor que no tenerlo.
 *
 * Declarado FUERA del componente, como `Modal` y `CampoCorregible`, por la razón de siempre
 * (BITACORA §3).
 */
function ValorDeCorreccion(
  { campo, valor, cual, onVer }: {
    campo: string;
    valor: string | null;
    cual: 'anterior' | 'nuevo';
    onVer: (path: string) => void;
  },
) {
  const esAnterior = cual === 'anterior';
  if (!esCampoDeFoto(campo)) {
    return esAnterior
      ? <span className="text-gray-500 line-through">{valor ?? '—'}</span>
      : <strong>{valor ?? '—'}</strong>;
  }
  if (valor === null || valor.trim() === '') {
    return <span className={esAnterior ? 'text-gray-500' : 'font-medium'}>sin foto</span>;
  }
  return (
    <button type="button" onClick={() => onVer(valor)} title={valor}
      className={esAnterior ? 'text-blue-600 underline' : 'font-medium text-blue-600 underline'}>
      {esAnterior ? 'ver la foto anterior' : 'ver la foto nueva'}
    </button>
  );
}

/**
 * Una de las dos fotos del modal: qué hay hoy, y el selector para poner otra.
 *
 * **Acepta galería además de cámara** (input sin `capture`) porque estas dos fotos son un DATO de
 * la operación: se corrigen desde el panel, en una computadora, con la foto que llegó por
 * WhatsApp o que se descargó del celular. Es lo contrario de la foto del marcado de asistencia,
 * que sigue siendo solo cámara: allí la foto no es un dato sino la prueba de que la persona
 * estaba en la tienda a esa hora, y elegirla de la galería dejaría marcar desde la casa con una
 * foto de otro día — que es justo lo que la geocerca existe para impedir (ver `CampoFoto`).
 *
 * Se muestra el estado actual y no solo el selector: "sin foto" contra "tiene foto" cambia lo que
 * uno está por hacer —agregar o reemplazar— y el enlace permite comprobar si de verdad hace falta.
 */
function CampoFotoCorreccion(
  { etiqueta, pathActual, elegida, onElegir, onVer }: {
    etiqueta: string;
    pathActual: string | null;
    elegida: File | null;
    onElegir: (f: File | null) => void;
    onVer: (path: string) => void;
  },
) {
  /**
   * Tipo y tope del bucket (0314), sobre el original: la compresión de `guardarCorreccion`
   * devuelve el original cuando falla, y entonces viaja tal cual.
   */
  const [rechazo, setRechazo] = useState<string | null>(null);
  return (
    <div>
      <span className="block text-sm font-medium">{etiqueta}</span>
      {pathActual === null ? (
        <span className="text-xs text-gray-500">Sin foto</span>
      ) : (
        <span className="text-xs text-gray-600">
          Tiene foto ·{' '}
          <button type="button" onClick={() => onVer(pathActual)}
            className="text-blue-600 underline">verla</button>
        </span>
      )}
      <input type="file" accept="image/*"
        aria-label={`${etiqueta}: elegir una nueva`}
        className="mt-1 block w-full text-xs"
        onChange={(e) => {
          const f = e.target.files?.[0] ?? null;
          const motivo = f === null ? null : rechazoDeArchivo(f);
          setRechazo(motivo);
          if (motivo !== null) { e.target.value = ''; onElegir(null); return; }
          onElegir(f);
        }} />
      {elegida !== null && (
        <span className="mt-0.5 block text-xs text-green-700">
          Se subirá «{elegida.name}» al guardar.
        </span>
      )}
      {rechazo !== null && (
        <span role="alert" className="mt-0.5 block text-xs text-red-600">{rechazo}</span>
      )}
    </div>
  );
}

/**
 * Modal declarado FUERA del componente a propósito: un componente definido dentro de otro se
 * remonta en cada render y el foco se pierde tecla a tecla (BITACORA §3, el formulario de
 * registro quedó inusable por eso y tiene pruebas de regresión).
 *
 * **Esta es LA capa de modal de la app**: `z-50` sobre el velo, `max-h-[90vh]` con scroll adentro.
 * Cualquier otro diálogo se alinea a estos dos valores en vez de estrenar los suyos.
 */
function Modal(
  { titulo, onCerrar, children }:
  { titulo: string; onCerrar: () => void; children: ReactNode },
) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded bg-white p-5 shadow-lg">
        <div className="mb-3 flex items-start justify-between">
          <h2 className="text-lg font-semibold">{titulo}</h2>
          <button type="button" onClick={onCerrar} aria-label="Cerrar"
            className="text-gray-400 hover:text-gray-700">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}
